import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import ClockIcon from "@/assets/colorable/clock.svg?react";
import CrownPlusIcon from "@/assets/colorable/crown_plus.svg?react";
import InfoIcon from "@/assets/colorable/info.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import CrownIcon from "@/assets/dark_gradient/crown.svg?react";
import UsersIcon from "@/assets/dark_gradient/users.svg?react";
import Button from "@/components/Common/Button";
import OverlappingAvatars from "@/components/Common/OverlappingAvatars";
import type {AmbassadorListInfluencerSerializer} from "@/generated/DjangoTypes";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {ambassadorProgramModalAtom} from "@/store/jotaiAtoms";
import {useNavigate} from "@tanstack/react-router";
import {useSetAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const YourAmbassadors: FC<{
  agencyId?: number | null;
  companyId?: number | null;
  label?: string;
  limit?: number;
  onOpenProgram?(): void;
}> = ({agencyId, companyId, label, limit = 15, onOpenProgram}) => {
  const {t} = useTranslation(["referral", "common"]);
  const navigate = useNavigate();
  const setAmbassadorProgramModal = useSetAtom(ambassadorProgramModalAtom);
  const openProgram = () =>
    onOpenProgram
      ? onOpenProgram()
      : setAmbassadorProgramModal({agencyId: agencyId ?? null, companyId: companyId ?? null, isOpen: true});
  const isAgency = !!agencyId && !companyId;

  const {data: agencyAmbassadorsData} = useGenericQuery<{
    ambassador_group_id: number | null;
    count: number;
    results: AmbassadorListInfluencerSerializer[];
  }>({
    queryKey: AGENCY_KEYS.qAgencyAmbassadors(agencyId!),
    queryFn: () => client.get(`${AGENCY_KEYS.qAgencyAmbassadors(agencyId!)[0]}?page_size=${limit}`),
    enabled: isAgency,
    refetchOnMount: true,
  });

  const {data: companyAmbassadorsData} = useGenericQuery<{
    ambassador_group_id: number | null;
    count: number;
    pending_count: number;
    results: AmbassadorListInfluencerSerializer[];
  }>({
    queryKey: COMPANY_KEYS.qCompanyAmbassadors(companyId!),
    queryFn: () => client.get(`${COMPANY_KEYS.qCompanyAmbassadors(companyId!)[0]}?page_size=${limit}`),
    enabled: !isAgency && !!companyId,
    refetchOnMount: true,
  });

  const acceptedCount = isAgency ? (agencyAmbassadorsData?.count ?? 0) : (companyAmbassadorsData?.count ?? 0);
  const pendingCount = isAgency ? 0 : (companyAmbassadorsData?.pending_count ?? 0);
  const ambassadorGroupId = isAgency
    ? agencyAmbassadorsData?.ambassador_group_id
    : companyAmbassadorsData?.ambassador_group_id;

  const pictures = (isAgency ? (agencyAmbassadorsData?.results ?? []) : (companyAmbassadorsData?.results ?? [])).map(
    a => a.picture320 ?? a.picture,
  );

  return (
    <div className="border-neutral-grey-light-dark flex flex-col gap-5 rounded-lg border bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <CrownIcon className="size-3.5 shrink-0" />
          <span className="text-neutral-black text-xl leading-4.5 font-semibold">{label ?? t("your_ambassadors")}</span>
          <span className="text-primary-dark-solid bg-neutral-grey-light-dark flex min-h-5 items-center justify-center rounded-full px-2 py-1 text-sm leading-2.5 font-semibold">
            {acceptedCount}
          </span>
          <div className="flex items-center gap-3 md:contents">
            {pendingCount > 0 && (
              <span
                onClick={() =>
                  ambassadorGroupId && navigate({to: "/group/$groupId", params: {groupId: ambassadorGroupId}})
                }
                className={clsxm(
                  "bg-warning-light-yellow text-warning-dark-yellow flex min-h-5 items-center gap-2 rounded-full px-2 py-1 text-sm leading-2.5 font-semibold whitespace-nowrap",
                  !!ambassadorGroupId && "cursor-pointer",
                )}>
                <ClockIcon className="size-3 shrink-0" />
                {t("pending_count", {count: pendingCount})}
              </span>
            )}
            <InfoIcon onClick={openProgram} className="text-primary-dark-solid size-3.5 shrink-0 cursor-pointer" />
          </div>
        </div>
        <Button buttonStyle="Gradient" className="h-8 w-fit gap-2.5 px-6" onClick={openProgram}>
          <CrownPlusIcon className="size-3.5 shrink-0 text-white" />
          {t("invite_ambassadors")}
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {acceptedCount > 0 ? (
          <OverlappingAvatars
            avatarClassName="border-2"
            keyPrefix="ambassador"
            pictures={pictures}
            totalCount={acceptedCount}
          />
        ) : (
          <div className="flex items-center gap-2">
            <div className="bg-neutral-grey-lightest flex size-15 shrink-0 items-center justify-center rounded-full">
              <UsersIcon className="text-primary-medium-solid size-5 shrink-0" />
            </div>
            <span className="text-neutral-grey-dark text-sm">{t("refer_more_creators_to_build_network")}</span>
          </div>
        )}
        {!!ambassadorGroupId && (
          <Button
            buttonStyle="Custom"
            className="flex h-8 gap-2.5 p-3 shadow-none enabled:hover:scale-[1.01] enabled:hover:shadow-none"
            onClick={() => navigate({to: "/group/$groupId", params: {groupId: ambassadorGroupId}})}>
            <span className="text-primary-dark-solid text-base font-medium">
              {acceptedCount > 0 ? t("common:view_all") : t("view_group")}
            </span>
            <RightArrowIcon className="text-primary-dark-solid size-3 shrink-0" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default YourAmbassadors;
